import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  OnInit,
  input
} from '@angular/core';
import { SchemaTranslations } from '../../../../shared/models/schema-translations';
import { Schema } from '../../../../core/_models/schema';
import {
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { AbstractControlValueAccessor } from '../../../../shared/abstract_control_value_accessor.abstract';

import { TranslatePipe } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-property-grouped-hash',
  templateUrl: './property-grouped-hash.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PropertyGroupedHashComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => PropertyGroupedHashComponent), multi: true },
  ],
  imports: [
    ReactiveFormsModule,
    TranslatePipe
],
})
export class PropertyGroupedHashComponent extends AbstractControlValueAccessor implements OnInit, Validator {
  private cdr = inject(ChangeDetectorRef);

  public readonly controlName = input<string>(undefined!);
  public readonly properties = input<Schema>(undefined!);
  public readonly schemaTranslations = input<SchemaTranslations | null>(undefined!);
  public readonly errors = input<any>();
  public groupedHash: any;
  public groupedKeys: string[];
  public groupedValues: string[];
  public required;
  public formGroup: UntypedFormGroup;
  private destroyRef = inject(DestroyRef);

  constructor() {
    const formBuilder = inject(UntypedFormBuilder);

    super();
    this.formGroup = formBuilder.group({ groupedKey: '', groupedValue: '' });
  }

  public ngOnInit(): void {
    this.formGroup.get('groupedKey')?.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe((data) => this.formGroupedKeyChange(data));
    this.formGroup.get('groupedValue')?.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe((data) => this.formGroupedValueChange(data));
    this.initializeProperty();
  }

  public writeValue(obj: any): void {
    if (!obj) { return; }
    this.formGroup.patchValue(this.formatGroupedHash(obj));
  }

  public validate(): ValidationErrors | any {
    return this.formGroup.errors;
  }

  private formGroupedKeyChange(data): void {
    this.groupedValues = this.groupedHash[data];
    this.formGroup.get('groupedValue')?.setValue('');
    this.cdr.markForCheck();
  }

  private formGroupedValueChange(data): void {
    this.onTouched();
    this.onChange([this.formGroup.get('groupedKey')?.value, data]);
  }

  private formatGroupedHash(obj: string[]): Record<string, string> {
    return { 'groupedKey': obj[0], 'groupedValue': obj[1] };
  }

  private initializeProperty(): void {
    this.groupedHash = this.properties().getGroupedHash(this.controlName());
    this.groupedKeys = Object.keys(this.groupedHash);
    this.required = this.properties().isFieldRequired(this.controlName());
  }
}
